import { BlockData, TimestampVerification, AnomalyFlag, AnomalyRecord } from '../types/bitcoin';
import { bitcoinApi } from './bitcoinApi';

// Simple median calculation function
function median(numbers: number[]): number {
  if (numbers.length === 0) return 0;
  if (numbers.length === 1) return numbers[0];
  
  const sorted = [...numbers].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  } else {
    return sorted[middle];
  }
}

export class AnomalyDetectionService {
  private readonly ANOMALY_THRESHOLD = 7200; // 2 hours in seconds
  private readonly CRITICAL_THRESHOLD = 14400; // 4 hours in seconds

  // Cross-verify timestamps from multiple sources (TypeScript conversion of Python script)
  async crossVerifyBlock(height: number): Promise<TimestampVerification> {
    console.log(`🔍 Cross-verifying block ${height} timestamps...`);
    
    // Fetch block data from all available sources
    const blockData = await bitcoinApi.fetchBlockFromAllSources(height);
    
    if (blockData.length === 0) {
      throw new Error(`No data sources available for block ${height}`);
    }

    // Extract timestamps from each source
    const sources: Record<string, number> = {};
    blockData.forEach(block => {
      sources[block.source] = block.timestamp;
    });

    // Calculate median timestamp
    const timestamps = Object.values(sources);
    const medianTimestamp = median(timestamps);

    // Calculate drift for each source
    const drift: Record<string, number> = {};
    const anomalies: AnomalyFlag[] = [];

    // Special handling for single source
    if (blockData.length === 1) {
      const source = blockData[0].source;
      drift[source] = 0; // No drift to calculate with single source
      
      // Add warning about single source
      anomalies.push({
        source: 'system',
        timestamp: medianTimestamp,
        drift: 0,
        severity: 'minor',
        message: `Only 1 data source available (${source}). Cross-verification requires multiple sources.`,
      });

      console.warn(`⚠️ Block ${height}: Single source verification (${source})`);
    } else {
      // Normal multi-source verification
      Object.entries(sources).forEach(([source, timestamp]) => {
        const driftSeconds = Math.abs(timestamp - medianTimestamp);
        drift[source] = driftSeconds;

        // Flag anomalies based on drift threshold
        if (driftSeconds > this.ANOMALY_THRESHOLD) {
          const severity = this.classifyAnomalySeverity(driftSeconds);
          const driftHours = (driftSeconds / 3600).toFixed(2);
          
          anomalies.push({
            source,
            timestamp,
            drift: driftSeconds,
            severity,
            message: `${source} drifts ${driftHours}h from median (${new Date(medianTimestamp * 1000).toISOString()})`,
          });

          console.warn(`⚠️ Block ${height}: ${source} drift ${driftSeconds}s (${driftHours}h)`);
        }
      });
    }

    const verification: TimestampVerification = {
      blockHeight: height,
      sources,
      median: medianTimestamp,
      anomalies,
      consensus: anomalies.length === 0 || (anomalies.length === 1 && anomalies[0].source === 'system'),
      drift,
    };

    // Log results
    if (verification.consensus && blockData.length > 1) {
      console.log(`✅ Block ${height}: Timestamp consensus achieved across ${Object.keys(sources).length} sources`);
    } else if (blockData.length === 1) {
      console.log(`⚠️ Block ${height}: Single source verification completed`);
    } else {
      console.log(`🚨 Block ${height}: ${anomalies.length} anomalies detected`);
    }

    return verification;
  }

  // Verify a range of blocks for historical analysis
  async verifyBlockRange(startHeight: number, endHeight: number): Promise<TimestampVerification[]> {
    console.log(`📊 Verifying block range ${startHeight} to ${endHeight}...`);
    
    const results: TimestampVerification[] = [];
    const delayBetweenRequests = 300; // 300ms to respect rate limits

    for (let height = startHeight; height <= endHeight; height++) {
      try {
        const verification = await this.crossVerifyBlock(height);
        results.push(verification);
        
        // Add delay to respect API rate limits
        if (height < endHeight) {
          await this.delay(delayBetweenRequests);
        }
      } catch (error) {
        console.error(`Failed to verify block ${height}:`, error);
        // Continue with next block
      }
    }

    const anomalyCount = results.reduce((count, result) => count + result.anomalies.length, 0);
    console.log(`📈 Block range verification complete: ${anomalyCount} anomalies found in ${results.length} blocks`);

    return results;
  }

  // Classify anomaly severity based on drift amount
  private classifyAnomalySeverity(driftSeconds: number): 'minor' | 'major' | 'critical' {
    if (driftSeconds >= this.CRITICAL_THRESHOLD) {
      return 'critical';
    } else if (driftSeconds >= this.ANOMALY_THRESHOLD * 1.5) {
      return 'major';
    } else {
      return 'minor';
    }
  }

  // Create an anomaly record for storage
  createAnomalyRecord(verification: TimestampVerification): AnomalyRecord | null {
    if (verification.consensus) {
      return null; // No anomaly to record
    }

    return {
      id: `${verification.blockHeight}-${Date.now()}`,
      blockHeight: verification.blockHeight,
      detectedAt: Date.now(),
      verification,
      resolved: false,
    };
  }

  // Calculate consensus health score (0-1)
  calculateConsensusScore(verifications: TimestampVerification[]): number {
    if (verifications.length === 0) return 1;
    
    const consensusBlocks = verifications.filter(v => v.consensus).length;
    return consensusBlocks / verifications.length;
  }

  // Get anomaly statistics
  getAnomalyStatistics(verifications: TimestampVerification[]): {
    totalBlocks: number;
    anomalyBlocks: number;
    totalAnomalies: number;
    severityBreakdown: Record<string, number>;
    sourceReliability: Record<string, number>;
  } {
    const totalBlocks = verifications.length;
    const anomalyBlocks = verifications.filter(v => !v.consensus).length;
    const totalAnomalies = verifications.reduce((sum, v) => sum + v.anomalies.length, 0);

    // Count anomalies by severity
    const severityBreakdown: Record<string, number> = { minor: 0, major: 0, critical: 0 };
    const sourceAnomalyCounts: Record<string, number> = {};
    const sourceTotalCounts: Record<string, number> = {};

    verifications.forEach(verification => {
      // Track source reliability
      Object.keys(verification.sources).forEach(source => {
        sourceTotalCounts[source] = (sourceTotalCounts[source] || 0) + 1;
      });

      verification.anomalies.forEach(anomaly => {
        severityBreakdown[anomaly.severity]++;
        sourceAnomalyCounts[anomaly.source] = (sourceAnomalyCounts[anomaly.source] || 0) + 1;
      });
    });

    // Calculate source reliability (percentage of blocks without anomalies)
    const sourceReliability: Record<string, number> = {};
    Object.keys(sourceTotalCounts).forEach(source => {
      const anomalyCount = sourceAnomalyCounts[source] || 0;
      const totalCount = sourceTotalCounts[source];
      sourceReliability[source] = ((totalCount - anomalyCount) / totalCount) * 100;
    });

    return {
      totalBlocks,
      anomalyBlocks,
      totalAnomalies,
      severityBreakdown,
      sourceReliability,
    };
  }

  // Helper method for delays
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Real-time anomaly detection for live monitoring
  async detectLiveAnomalies(blockHeight: number): Promise<AnomalyRecord[]> {
    try {
      const verification = await this.crossVerifyBlock(blockHeight);
      const record = this.createAnomalyRecord(verification);
      
      if (record) {
        // Emit anomaly event for components to listen to
        window.dispatchEvent(new CustomEvent('anomalyDetected', { 
          detail: { record, verification } 
        }));
        
        return [record];
      }
      
      return [];
    } catch (error) {
      console.error(`Live anomaly detection failed for block ${blockHeight}:`, error);
      return [];
    }
  }
}

// Export singleton instance
export const anomalyDetection = new AnomalyDetectionService();